import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useSpacetimeDB } from "spacetimedb/tanstack";
import { match } from "ts-pattern";

import { toaster } from "@/web/components/primitives/toast";
import { FullPageLoader } from "@/web/components/styled/FullPageLoader";
import type { FormNavigationStage } from "@/web/hooks/useFormNavigation";
import { useFormNavigation } from "@/web/hooks/useFormNavigation";
import { ErrorHandler } from "@/web/lib/errors";
import { isAuthError, isNotFoundError } from "@/web/lib/spacetimedbErrors";
import type { DbConnection } from "@tycho/web-bindings";
import type { LandlordNegotiationFormRow } from "@tycho/web-bindings/types";

import { AccessCodeInput } from "./-AccessCodeInput";
import { Confirmation } from "./-Confirmation";
import { FullSale } from "./-FullSale";
import { Intro } from "./-Intro";
import { PartialSale } from "./-PartialSale";
import { Submitted } from "./-Submitted";

export const Route = createFileRoute("/landlord/$underwritableEntity")({
  component: RouteComponent,
});

export type NegotiationStage = "intro" | "partial" | "full" | "confirm";
export type SaleType = "partial" | "full" | undefined;

export type LandlordFormInput = {
  saleType: SaleType;
  setSaleType: (v: SaleType) => void;
  minPurchasePrice: number | undefined;
  setMinPurchasePrice: (v: number | undefined) => void;
  fairPurchasePrice: number | undefined;
  setFairPurchasePrice: (v: number | undefined) => void;
  cashOut: number | undefined;
  setCashOut: (v: number | undefined) => void;
  monthlyDividend: number | undefined;
  setMonthlyDividend: (v: number | undefined) => void;
  rentalYield: number | undefined;
  setRentalYield: (v: number | undefined) => void;
};

export type LandlordFormDerived = {
  impliedPurchasePrice: number | undefined;
};

export type NegotiationHookReturn = {
  stage: FormNavigationStage<NegotiationStage>;
  negotiation: LandlordNegotiationFormRow;
};

const accessCodeStorageKey = (entity: string) => `landlord-negotiation-access-code:${entity}`;

function readPersistedAccessCode(entity: string): string {
  try {
    return window.localStorage.getItem(accessCodeStorageKey(entity)) ?? "";
  } catch (error: unknown) {
    ErrorHandler.logError(error);
    return "";
  }
}

function persistAccessCode(entity: string, code: string) {
  try {
    window.localStorage.setItem(accessCodeStorageKey(entity), code);
  } catch (error: unknown) {
    ErrorHandler.logError(error);
    // localStorage can be unavailable (Safari private mode); not blocking.
  }
}

function clearPersistedAccessCode(entity: string) {
  try {
    window.localStorage.removeItem(accessCodeStorageKey(entity));
  } catch (error: unknown) {
    ErrorHandler.logError(error);
    // localStorage can be unavailable (Safari private mode); not blocking.
  }
}

function RouteComponent() {
  const { underwritableEntity: entityStr } = Route.useParams();
  // oxlint-disable-next-line unbound-method - spacetime booboo
  const { getConnection } = useSpacetimeDB();

  const [accessCode, setAccessCode] = useState(() => readPersistedAccessCode(entityStr));
  const [submittedLocally, setSubmittedLocally] = useState(false);

  const [saleType, setSaleType] = useState<SaleType>(undefined);
  const [minPurchasePrice, setMinPurchasePrice] = useState<number | undefined>(undefined);
  const [fairPurchasePrice, setFairPurchasePrice] = useState<number | undefined>(undefined);
  const [cashOut, setCashOut] = useState<number | undefined>(undefined);
  const [monthlyDividend, setMonthlyDividend] = useState<number | undefined>(undefined);
  const [rentalYield, setRentalYield] = useState<number | undefined>(undefined);

  const formQuery = useQuery({
    queryKey: ["landlord-form", entityStr, accessCode],
    queryFn: async () => {
      const conn = getConnection() as DbConnection | null;
      if (!conn) throw new Error("Not connected to server.");
      return conn.procedures.getLandlordNegotiationForm({
        accessCode,
      });
    },
    enabled: accessCode.length > 0,
    retry: false,
  });

  useEffect(() => {
    if (formQuery.isSuccess && accessCode.length > 0) {
      persistAccessCode(entityStr, accessCode);
    }
  }, [formQuery.isSuccess, accessCode, entityStr]);

  useEffect(() => {
    if (isAuthError(formQuery.error)) {
      clearPersistedAccessCode(entityStr);
    }
  }, [formQuery.error, entityStr]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const conn = getConnection() as DbConnection | null;
      if (!conn) throw new Error("Not connected to server.");
      if (saleType === "full") {
        if (minPurchasePrice === undefined || fairPurchasePrice === undefined) {
          throw new Error("Fill in both prices first.");
        }
        await conn.procedures.submitLandlordNegotiationForm({
          accessCode,
          data: { tag: "Full", value: { minPurchasePrice, fairPurchasePrice } },
        });
        return;
      }
      if (saleType === "partial") {
        if (cashOut === undefined || monthlyDividend === undefined || rentalYield === undefined) {
          throw new Error("Fill in all three fields first.");
        }
        await conn.procedures.submitLandlordNegotiationForm({
          accessCode,
          data: {
            tag: "Partial",
            value: {
              usdCashOut: cashOut,
              usdMonthlyDividend: monthlyDividend,
              percentAnnualRentalYield: rentalYield,
            },
          },
        });
        return;
      }
      throw new Error("Pick a sale type first.");
    },
    onSuccess: () => {
      setSubmittedLocally(true);
      toaster({ message: "Thanks — your response was sent.", variant: "success" });
    },
    onError: ErrorHandler.default,
  });

  const { stage } = useFormNavigation<NegotiationStage>("intro");

  const input: LandlordFormInput = {
    saleType,
    setSaleType,
    minPurchasePrice,
    setMinPurchasePrice,
    fairPurchasePrice,
    setFairPurchasePrice,
    cashOut,
    setCashOut,
    monthlyDividend,
    setMonthlyDividend,
    rentalYield,
    setRentalYield,
  };
  const derived: LandlordFormDerived = {
    impliedPurchasePrice:
      cashOut !== undefined &&
      monthlyDividend !== undefined &&
      rentalYield !== undefined &&
      rentalYield > 0
        ? (12 * monthlyDividend) / (rentalYield / 100) + cashOut
        : undefined,
  };

  function handleAccessCodeSubmit(code: string) {
    setAccessCode(code);
  }

  if (!accessCode || isAuthError(formQuery.error)) {
    return (
      <AccessCodeInput onSubmit={handleAccessCodeSubmit} hasError={isAuthError(formQuery.error)} />
    );
  }
  if (isNotFoundError(formQuery.error)) {
    return (
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="withus-strong text-2xl">Negotiation not found</h1>
        <p>Double-check the link in your email and try again.</p>
      </div>
    );
  }
  if (formQuery.isLoading || !formQuery.data) {
    return <FullPageLoader />;
  }

  const negotiation = formQuery.data;
  const submitted = submittedLocally || negotiation.submittedAt !== undefined;

  if (submitted) {
    return <Submitted />;
  }

  return (
    <div className="min-h-screen w-full bg-background">
      {match(stage.current)
        .with("intro", () => <Intro stage={stage} negotiation={negotiation} input={input} />)
        .with("full", () => <FullSale stage={stage} negotiation={negotiation} input={input} />)
        .with("partial", () => <PartialSale stage={stage} input={input} derived={derived} />)
        .with("confirm", () => (
          <Confirmation
            stage={stage}
            negotiation={negotiation}
            input={input}
            derived={derived}
            submit={() => submitMutation.mutate()}
            isSubmitting={submitMutation.isPending}
          />
        ))
        .exhaustive()}
    </div>
  );
}
